import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";

export const NOTE_MAX_LENGTH = 1000;

export interface NoteDialogFact {
  label: string;
  value: string;
}

interface NoteDialogProps {
  open: boolean;
  title: string;
  facts: NoteDialogFact[];
  description?: string;
  confirmLabel: string;
  confirmColor?: "primary" | "warning" | "error";
  loading: boolean;
  onClose: () => void;
  onConfirm: (note: string | null) => void;
}

/**
 * A confirmation that names the bill it acts on and takes an optional note — shared by
 * "ไม่ส่งอัตโนมัติ" and "รับทราบ", which both record who did it, when, and why.
 */
export default function NoteDialog({
  open,
  title,
  facts,
  description,
  confirmLabel,
  confirmColor = "primary",
  loading,
  onClose,
  onConfirm,
}: NoteDialogProps) {
  const [note, setNote] = useState<string>("");

  // A fresh draft every time: a note typed for one bill must never ride along to the next.
  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  const tooLong = note.length > NOTE_MAX_LENGTH;

  const handleConfirm = () => {
    if (tooLong) return;
    const trimmed = note.trim();
    onConfirm(trimmed ? trimmed : null);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {facts.map(({ label, value }) => (
          <Box key={label} sx={{ display: "flex", gap: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96 }}>
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
              {value}
            </Typography>
          </Box>
        ))}
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {description}
          </Typography>
        )}
        <TextField
          label="หมายเหตุ (ไม่บังคับ)"
          multiline
          minRows={3}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={tooLong}
          helperText={
            tooLong
              ? `หมายเหตุต้องไม่เกิน ${NOTE_MAX_LENGTH} ตัวอักษร`
              : `${note.length}/${NOTE_MAX_LENGTH}`
          }
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          ปิด
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={handleConfirm}
          disabled={tooLong}
          loading={loading}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
